// @flow
import {SubmissionError, Field, type FormProps} from 'redux-form'
import {compose} from 'redux'
import React, {Component} from 'react'
import MediaQuery from 'react-responsive'
import {Interpolate} from 'react-i18next'
import PropTypes from 'prop-types'
import css from './index.css'
import grid from '$src/grid/grid.css'
import amplitude from '$src/analytics/amplitude'

import refreshTokens from '$src/auth/sagas/refreshTokens'
import cancel from '$src/login/sagas/cancel'
import TextInput from '$common/components/TextInput'
import Header from '$common/components/Header'
import PasswordInput from '$common/components/PasswordInput'
import GenericButton from '$common/components/GenericButton'
import ErrorCandy from '$common/components/ErrorCandy'
import LoadingScreen from '$common/components/LoadingScreen'
import loginSaga from '$src/auth/sagas/login'
import {selectIdToken, selectRefreshToken} from '$src/auth/selectors'
import {isFetching, selectApiErrorMessage} from '$src/requests/selectors'
import clearApiError from '$src/requests/sagas/clearApiError'
import {
  resolveCss,
  withTranslate,
  asForm,
  createApiErrorMessage,
  type WithTranslateProps,
} from '$util'

type Props = WithTranslateProps &
  FormProps & {
    refreshingTokens: ?boolean,
    isCanceling: ?boolean,
    idToken: ?string,
    refreshToken: ?string,
    apiError: ?string
  }

export class Login extends Component<Props> {
  componentWillMount() {
    if (this.props.idToken && this.props.refreshToken) {
      this.context.runSaga(refreshTokens)
    }
  }

  onSuccess = () => {
    this.props.history.replace('/consent' + this.props.history.location.search)
  }

  login = async (values: {email: string, password: string}) => {
    try {
      amplitude.logEvent('[UI] Login::login')
      await this.context.runSaga(loginSaga, values)
    } catch (e) {
      const errMsg = createApiErrorMessage(e)
      throw new SubmissionError({error: this.props.t(errMsg)})
    }
  }

  log = (eventName: string) => () => amplitude.logEvent(eventName)

  cancel = () => {
    amplitude.logEvent('[UI] Login::cancel')
    this.context
      .runSaga(cancel, this.props.history.location.search)
      .then((res: {data: {url: string}}) => {
        document.location.replace(res.data.url)
      })
  }

  clearApiError = () => this.context.runSaga(clearApiError)

  render() {
    const classes = resolveCss(css, grid)

    const registration = (
      <a onClick={this.log('[UI] Login::signupLink')} href={this.props.t('link.signup')} target="_blank">
        {this.props.t('login.signupHere')}
      </a>
    )

    const titleAndBody = [
      <h3 key="0" className={css.title}>
        {this.props.t('login.title')}
      </h3>,
      <p key="1" className={css.body}>
        <Interpolate i18nKey="login.signupText" signup={registration} />
      </p>,
    ]

    return (
      <LoadingScreen
        onSuccess={this.onSuccess}
        loading={this.props.refreshingTokens || this.props.submitting}
        error={this.props.submitFailed}
      >
        <div {...classes('loginContainer', 'container')} data-tid='page-login'>
          <Header />

          <form
            {...classes('formContainer', 'columnLarge6')}
            onSubmit={this.props.handleSubmit(this.login)}
          >
            <div {...classes('fieldsContainer')}>
              <MediaQuery maxWidth={767}>
                {isMobile => (
                  <div>
                    {isMobile ? [...titleAndBody].reverse() : titleAndBody}
                  </div>
                )}
              </MediaQuery>
              <Field
                data-tid='email'
                type="email"
                component={TextInput}
                placeholder={this.props.t('common.emailPlaceholder')}
                label={this.props.t('common.emailAddress')}
                name="email"
              />
              <Field
                data-tid='password'
                component={PasswordInput}
                placeholder={this.props.t('common.passwordPlaceholder')}
                label={this.props.t('common.password')}
                name="password"
              />
            </div>
            <div {...classes('buttonContainer')}>
              <GenericButton
                loading={this.props.isCanceling}
                type="button"
                onClick={this.cancel}
                css={{container: [css.button, css.cancelButton]}}
                style="outline"
                data-tid='login-cancel'
              >
                {this.props.t('common.cancel')}
              </GenericButton>

              <GenericButton
                type="submit"
                css={{container: css.button}}
                style="bold"
                data-tid='login-submit'
              >
                {this.props.t('login.cta')}
              </GenericButton>
            </div>
            <div {...classes('footer')}>
              <a onClick={this.log('[UI] Login::aboutLink')} href={this.props.t('link.about')} target="_blank">
                {this.props.t('login.about')}
              </a>
              <a onClick={this.log('[UI] Login::signupLink')} href={this.props.t('link.signup')} target="_blank">
                {this.props.t('login.signupHere')}
              </a>
              <a onClick={this.log('[UI] Login::faqLink')} href={this.props.t('link.faq')} target="_blank">
                {this.props.t('login.faq')}
              </a>
            </div>
          </form>
          <ErrorCandy
            autoHide
            show={this.props.submitFailed || !!this.props.apiError}
            onHide={this.props.submitFailed
              ? this.props.initWithValues
              : this.clearApiError}>
            {(this.props.submitErrors && this.props.submitErrors.error) || this.props.apiError}
          </ErrorCandy>
        </div>
      </LoadingScreen>
    )
  }
}

Login.contextTypes = {
  runSaga: PropTypes.any,
}

export default compose(
  withTranslate(),
  asForm('login', {
    stateToProps: (state, ownProps: WithTranslateProps) => {
      const apiErrMsg = selectApiErrorMessage(state)
      return {
        isCanceling: isFetching(state, 'cancel'),
        idToken: selectIdToken(state),
        refreshToken: selectRefreshToken(state),
        refreshingTokens: isFetching(state, 'refreshTokens'),
        apiError: apiErrMsg && ownProps.t(apiErrMsg),
      }
    },
  })
)(Login)



// WEBPACK FOOTER //
// ./src/login/components/Login/index.js